/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { cssMap as unboundedCssMap } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import strikeThrough from './assets/strike-through.svg';
import { messages } from './messages';

const outerFrameStyles = cssMap({
	root: {
		backgroundColor: token('color.background.accent.gray.subtlest'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.300'),
		height: '185px',
	},
});

const premiumPriceStyles = unboundedCssMap({
	root: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '48px',
		fontWeight: token('font.weight.bold'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '56px' /* 116.667% */,
		minWidth: '90px',
		position: 'relative',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
});

const strikeThroughStyles = unboundedCssMap({
	root: {
		top: '-27px',
		left: '35px',
		maxWidth: '0px',
		maxHeight: '0px',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		zIndex: 1,
		position: 'relative',
	},
});

const purpleStyles = unboundedCssMap({
	root: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#AF59E0',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '48px',
		fontWeight: token('font.weight.bold'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '56px' /* 116.667% */,
	},
});

/**
 * This depicts the left side modal as shown here:
 * https://www.figma.com/design/gN2uHK0wPD93kkqExPlNeM/Confluence%3A-Recommend-Standard-to-RT-trialing-customers-(RT)?node-id=3730-11924&node-type=frame&t=ZS7oExyVxcrlRkJQ-0
 */
export const PriceContrast = ({
	standardPrice,
	premiumPrice,
}: {
	premiumPrice: number;
	standardPrice: number;
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
	const formattedStandardPrice = currencyFormatter.format(standardPrice);
	const formattedPremiumPrice = currencyFormatter.format(premiumPrice);

	return (
		<Flex xcss={outerFrameStyles.root} gap="space.300" alignItems="center" justifyContent="center">
			<div css={strikeThroughStyles.root}>
				<img src={strikeThrough} alt="strike through" />
			</div>
			<Flex direction="column">
				<div css={premiumPriceStyles.root}>{formattedPremiumPrice}</div>
				<Text size="small" color="color.text.accent.gray">
					{formatMessage(messages.perUserPerMonth)}
				</Text>
				<Text>
					<FormattedMessage {...messages.premiumEdition} />
				</Text>
			</Flex>
			<Stack>
				<Box>
					<span css={purpleStyles.root}>{formattedStandardPrice}</span>
				</Box>
				<Text size="small" color="color.text.accent.gray">
					{formatMessage(messages.perUserPerMonth)}
				</Text>
				<Text>
					<FormattedMessage {...messages.standardEdition} />
				</Text>
			</Stack>
		</Flex>
	);
};
